import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminTabs from '@/features/admin/components/AdminTabs';
import { DataTable } from '@/shared/ui/DataTable';
import { createDataTableColumns } from '@/shared/ui/dataTableFeatures';
import { usePaginatedList } from '@/shared/hooks/usePaginatedList';
import { listCreditGrants } from '@/features/admin/services/adminService';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import type { CreditGrant } from '@/shared/types';

const PAGE_SIZE = 200;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
}

const grantsColumnHelper = createDataTableColumns<CreditGrant>();

const grantsColumns = grantsColumnHelper.columns([
  grantsColumnHelper.accessor((g) => formatDateTime(g.createdAt), {
    id: 'createdAt',
    header: 'Date',
    filterFn: 'includesString',
  }),
  grantsColumnHelper.accessor((g) => g.userEmail || g.userId, {
    id: 'user',
    header: 'User',
    filterFn: 'includesString',
    cell: ({ row }) => {
      const g = row.original;
      return (
        <>
          <Link to={`/admin?q=${encodeURIComponent(g.userEmail || g.userId)}`}>{g.userEmail || g.userId}</Link>
          {g.userName ? <div className="field-note">{g.userName}</div> : null}
        </>
      );
    },
  }),
  grantsColumnHelper.accessor('amountCredits', {
    header: 'Amount',
    filterFn: 'includesString',
    cell: (info) => `${info.getValue()} cr`,
  }),
  grantsColumnHelper.accessor('label', {
    header: 'Label',
    filterFn: 'includesString',
  }),
  grantsColumnHelper.accessor('reason', {
    header: 'Reason',
    enableSorting: false,
    filterFn: 'includesString',
    cell: (info) => {
      const reason = info.getValue();
      return <span title={reason || ''}>{reason || '—'}</span>;
    },
  }),
  grantsColumnHelper.accessor('grantedByName', {
    header: 'Granted by',
    filterFn: 'includesString',
  }),
]);

export default function AdminCreditGrantsPage() {
  const { items: grants, loading, loadingMore, error, setError, hasMore, load, loadMore } =
    usePaginatedList<CreditGrant>((nextOffset) =>
      listCreditGrants({
        limit: PAGE_SIZE,
        offset: nextOffset,
      }).then((page) => ({ items: page.grants, total: page.total, offset: page.offset })),
    );

  useEffect(() => {
    load(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="admin-users">
      <AdminTabs />

      <div className="admin-header">
        <h1>Grant History</h1>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
        </div>
      ) : error && grants.length === 0 ? (
        <ErrorBanner message={error} />
      ) : grants.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__title">No credit grants found</div>
        </div>
      ) : (
        <>
          <DataTable
            columns={grantsColumns}
            data={grants}
            getRowId={(g) => g.id}
            enablePagination={true}
            pageSize={20}
          />

          {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
          {hasMore && (
            <button
              type="button"
              className="button secondary wallet-activity__more"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Load more from server'}
            </button>
          )}
        </>
      )}
    </section>
  );
}
